import { fmtEur0, fmtUsd0 } from '../formatters.js';
import CardMockup from './CardMockup.jsx';

const CARD_VARIANT = { free: 'black', pro: 'glass', premium: 'platinum' };

const PREMIUM_TIER = {
  displayName: 'Premium',
  price: 50,
  priceAnnual: 0,
};

// Headline copy templated from tier values so the Simple page reflects whatever
// the Detail-mode editor tunes. Static bullets that don't depend on values
// (e.g., "Visa card included", "Multi-currency IBAN") stay as constants below.

const STATIC_BULLETS = {
  free: ['5 smart rules', 'Money Back eligibility'],
  pro: [
    'Unlimited smart rules',
    'Travel mode',
    'Money Back boosts',
    'Silver physical card included',
  ],
  premium: [
    'Higher card and wallet limits',
    'Priority support',
    'Premium rewards and partner perks',
    'Dedicated physical card package',
    'Early access to advanced AI/payment tools',
  ],
};

const HEADLINES = {
  free: 'Try the card.',
  pro: 'Pays itself back on one foreign trip.',
  premium: 'A higher-touch package for power users.',
};

export default function Plans({ tiers, stablecoin }) {
  return (
    <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
      <PlanCard tier={tiers.free} kind="free" stablecoin={stablecoin} />
      <PlanCard tier={tiers.pro} kind="pro" badge="most popular" stablecoin={stablecoin} />
      <PlanCard tier={PREMIUM_TIER} kind="premium" badge="coming soon" stablecoin={stablecoin} />
    </div>
  );
}

function dynamicBullets(tier, kind, stablecoin) {
  if (kind === 'premium') {
    return STATIC_BULLETS.premium || [];
  }

  const lines = [];

  if (kind === 'free') {
    lines.push('Instant virtual card');
  }

  // FX
  if (kind === 'free') {
    lines.push(
      `No FX fees up to ${fmtUsd0(tier.fxCap)} / mo, ${(tier.fxAboveRate * 100).toFixed(1)}% above`,
    );
  } else if (tier.fxAboveRate < 0.01) {
    lines.push(`No FX fees up to ${fmtUsd0(tier.fxCap)} / mo`);
  } else {
    lines.push(
      `No FX fees up to ${fmtUsd0(tier.fxCap)} / mo, ${(tier.fxAboveRate * 100).toFixed(1)}% above`,
    );
  }

  // Balance conversion. The underlying model keeps "swap" as the internal
  // variable name, but plan copy stays non-crypto for users.
  if (kind === 'free') {
    lines.push(`Balance conversions at ${(tier.swapAboveRate * 100).toFixed(1)}%`);
  } else {
    lines.push(`Free balance conversions up to ${fmtEur0(tier.swapCap)} / mo`);
  }

  // Earn
  const earnDisplayed = Math.max(
    0,
    (stablecoin?.smartContractYieldApy ?? tier.earnApy) - (tier.pulsarYieldSpread ?? 0),
  );
  if (tier.earnApyPointsBonus > 0) {
    lines.push(
      `${(earnDisplayed * 100).toFixed(1)}% Earn + ${(tier.earnApyPointsBonus * 100).toFixed(1)}% Points`,
    );
  } else {
    lines.push(`${(earnDisplayed * 100).toFixed(1)}% Earn APY`);
  }

  // AI assistant usage
  if (tier.aiAgentRunsIncluded > 0) {
    lines.push(`${tier.aiAgentRunsIncluded} AI assistant actions / month`);
  }

  return [...lines, ...(STATIC_BULLETS[kind] || [])];
}

function PlanCard({ tier, kind, badge, stablecoin }) {
  const stylesByKind = {
    free: 'plan-card-shell plan-card-free',
    pro: 'plan-card-shell plan-card-pro',
    premium: 'plan-card-shell plan-card-premium text-white',
  };
  const muted = kind === 'premium' ? 'text-carbon-300' : 'text-carbon-500';
  const tagBg =
    kind === 'premium'
      ? 'right-6 bg-mint-300 text-carbon-900'
      : 'left-1/2 -translate-x-1/2 bg-carbon-800 text-white';

  return (
    <div className={`relative rounded-card ${stylesByKind[kind]} p-7 md:p-8 anim-up`}>
      {badge && (
        <span
          className={`absolute -top-3 text-[10px] font-medium uppercase tracking-caps ${tagBg} rounded-full px-3 py-1.5`}
        >
          {badge}
        </span>
      )}

      <CardMockup variant={CARD_VARIANT[kind]} className="mb-7" />

      <p className={kind === 'premium' ? 'caption text-mint-300' : 'caption text-ice-700'}>
        {tier.displayName}
      </p>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-5xl font-semibold tracking-h1 num-tabular leading-none">
          {tier.price === 0 ? 'Free' : fmtEur0(tier.price)}
        </span>
        {tier.price > 0 && <span className={`text-base ${muted}`}>/ month</span>}
      </div>
      {tier.priceAnnual > 0 && (
        <p className={`text-xs mt-2 ${muted}`}>
          or {fmtEur0(tier.priceAnnual)} / year (save{' '}
          {(((tier.price * 12 - tier.priceAnnual) / (tier.price * 12)) * 100).toFixed(0)}%)
        </p>
      )}

      <p className={`body-md mt-5 ${muted}`}>{HEADLINES[kind]}</p>

      <ul className="mt-6 space-y-2.5">
        {dynamicBullets(tier, kind, stablecoin).map((b, i) => (
          <li key={i} className="flex gap-2 text-sm leading-snug">
            <Checkmark />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Checkmark() {
  return (
    <svg
      className="flex-shrink-0 mt-0.5 text-ice-700"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M3 8.5l3 3L13 4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
